import { DashboardFilter } from '../../filters/dashboard_filter';
import { SingleSelectParentSelectChildFilterSettings } from '../../filters/dashboard_filter_parent_filter_settings';
import {
  DashboardSingleSelectFilter,
  SingleSelectFilterSettings,
  SingleSelectFilterUISettings,
} from '../../filters/dashboard_select_filter';
import { Granularity, Metric } from '../dashboard-config.model';
import { MetricProfile } from '../metrics/metric-profile';
import { isKpiAvailableAt, KPI_FILTER_NAME, KPI_OPTIONS, kpisOfMetric, METRIC_ID_FILTER_NAME } from './kpi-options';

export { KPI_FILTER_NAME, METRIC_ID_FILTER_NAME };

/** Option key the `kpi` filter matches against the parent `metric_id` value. */
const KPI_PARENT_KEY = 'metric_id';

/**
 * Hidden parent of the `kpi` filter. Holds the dashboard's metric as a string
 * (filter values are strings). Top-down dashboards pass the metric from the
 * dashboard id; a metric change navigates, so the value never changes there.
 *
 * Named `metric_id` because `metric` is the legacy company-type filter.
 */
export class DashboardMetricIdSingleSelectFilter extends DashboardSingleSelectFilter<string> {
  constructor({ metricId }: { metricId: Metric }) {
    super({
      name: METRIC_ID_FILTER_NAME,
      options: Object.values(Metric)
        .filter((value): value is Metric => typeof value === 'number')
        .map((value) => ({ label: Metric[value], value: `${value}` })),
      settings: new SingleSelectFilterSettings({}),
      uiSettings: new SingleSelectFilterUISettings({ label: 'Kennzahl', showInFilterComponent: false }),
      initialValue: `${metricId}`,
    });
  }
}

/**
 * One filter for every metric's KPI selection (replaces the per-metric type
 * filters). Value is a KPI id from KPI_OPTIONS, i.e. the dataset column stem.
 * Options are narrowed by the parent `metric_id` and by granularity availability.
 */
export class DashboardKpiSingleSelectFilter extends DashboardSingleSelectFilter<string> {
  constructor({ metricId, label, granularity, showInFilterComponent = true }: {
    /** Initial value and visibility: a metric with a single KPI hides the filter. */
    metricId: Metric;
    label: string;
    granularity?: Granularity;
    showInFilterComponent?: boolean;
  }) {
    const metricKpis = kpisOfMetric(metricId).filter((option) => isKpiAvailableAt(option, granularity));
    // Hide only for metrics without a type choice at all. A one-option list caused by
    // granularity availability stays visible, like the legacy type filters.
    const isSingleKpi = kpisOfMetric(metricId).length <= 1;

    super({
      name: KPI_FILTER_NAME,
      options: KPI_OPTIONS
        .filter((option) => isKpiAvailableAt(option, granularity))
        .map((option) => ({ label: option.label, value: option.value, [KPI_PARENT_KEY]: `${option.metricId}` })),
      parentFilterSettings: new SingleSelectParentSelectChildFilterSettings({
        parentFilterName: METRIC_ID_FILTER_NAME,
        filterByKey: KPI_PARENT_KEY,
      }),
      settings: new SingleSelectFilterSettings({}),
      uiSettings: new SingleSelectFilterUISettings({
        label,
        showInFilterComponent: showInFilterComponent && !isSingleKpi,
      }),
      initialValue: metricKpis[0]?.value,
    });
  }

  /**
   * Parent narrowing and the reset on parent change only run while `options$` has a
   * subscriber, which is normally the dropdown. Subscribe here so a hidden KPI filter
   * still follows `metric_id`. The subscription lives exactly as long as the filters.
   */
  override initialize() {
    super.initialize();
    this.options$.subscribe();
  }
}

/** `metric_id` parent + `kpi` child for a metric at a granularity, in controller order. */
export function createKpiFilters(
  metric: MetricProfile,
  granularity?: Granularity,
  options?: { showInFilterComponent?: boolean },
): DashboardFilter<any>[] {
  return [
    new DashboardMetricIdSingleSelectFilter({ metricId: metric.id }),
    new DashboardKpiSingleSelectFilter({
      metricId: metric.id,
      label: metric.labels.typeFilterTitle ?? metric.labels.shortLabel,
      granularity,
      showInFilterComponent: options?.showInFilterComponent,
    }),
  ];
}
